import { useState } from 'react';
import { Check, ChevronRight, Plus } from 'lucide-react';
import { localeTagFromLang } from '../../utils/bookingLocaleDisplay';
import './BookingComponents.css';
import './booking-brand.css';

export interface ActiveBundleVM {
  id: string;
  sessionsTotal: number;
  sessionsConsumed: number;
  sessionsRemaining: number;
  expiresAt: string;
  status: string;
}

export interface AvailableBundleVM {
  id: string;
  name: string;
  session_count: number;
  validity_days: number;
  price: number;
  currency: string;
  payment_mode: string;
  description?: string;
}

/** Movimiento de un bono, ya formateado por quien lo pinta (motivo traducido y fecha legible). */
export interface BundleHistoryRowVM {
  id: string;
  delta: number;
  label: string;
  date: string;
}

interface BundlePurchaseProps {
  myBundles: ActiveBundleVM[];
  availableBundles: AvailableBundleVM[];
  language?: string;
  t: (key: string, options?: Record<string, string | number>) => string;
  onPurchase: (bundle: AvailableBundleVM) => void;
  onBack: () => void;
  /** Movimientos por id de bono activo. Sin entradas, no se ofrece desplegar historial. */
  history?: Record<string, BundleHistoryRowVM[]>;
  /** Compra en curso: bloquea el CTA para no crear dos checkouts. */
  busy?: boolean;
}

/** "5, 10 o 20": la conjunción viene de i18n porque cambia con el idioma. */
function formatList(values: number[], or: string): string {
  if (values.length <= 1) return values.join('');
  return `${values.slice(0, -1).join(', ')} ${or} ${values[values.length - 1]}`;
}

function money(amount: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: (currency || 'EUR').toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

/**
 * Bonos de sesiones. Dos vistas en una, como el diseño de referencia:
 *
 * - "Mis bonos": el estado. Cada bono activo muestra las sesiones que quedan como una barra
 *   segmentada — un segmento por sesión — porque un bono se gasta de una en una y un porcentaje
 *   no dice cuántas quedan. Comprar es una fila discreta, no la acción principal.
 * - "Comprar": el catálogo. Aparece directamente cuando no hay ningún bono activo (no hay estado
 *   que enseñar) o al pulsar "comprar otro bono".
 *
 * Se llega aquí desde el menú de perfil, el reclamo del paso 1 y la opción de pago del paso 3.
 */
export function BundlePurchase({
  myBundles,
  availableBundles,
  language = 'es',
  t,
  onPurchase,
  onBack,
  history,
  busy = false,
}: BundlePurchaseProps) {
  const locale = localeTagFromLang(language);
  const [openHistory, setOpenHistory] = useState<string | null>(null);
  const hasActive = myBundles.length > 0;
  const canBuy = availableBundles.length > 0;
  const [buying, setBuying] = useState(!hasActive);
  // Pack recomendado preseleccionado: la pantalla necesita una acción primaria concreta.
  const recommendedIndex = availableBundles.length > 1 ? 1 : 0;
  /**
   * `useState` solo mira la lista una vez, en el primer render. Si los bonos llegan después
   * (lo normal: la pantalla se pinta antes de que responda el catálogo) la elección se quedaba
   * en `null` y el botón deshabilitado; y lo que el cliente hubiera marcado desaparecía en
   * cuanto algo volviera a montar el componente, devolviéndolo al pack «recomendado» —el
   * camino por el que QA elegía el Pack 5 y acababa comprando el Pack 20—.
   *
   * Lo elegido manda siempre que siga en la lista; si no está (todavía no cargó, o cambió el
   * catálogo), se cae al recomendado. Sin efectos: se deriva en el render.
   */
  const [pickedId, setPickedId] = useState<string | null>(null);
  const selected =
    availableBundles.find((b) => b.id === pickedId) ??
    availableBundles[recommendedIndex] ??
    null;
  const selectedId = selected?.id ?? null;
  const setSelectedId = setPickedId;

  const showCatalog = buying || !hasActive;

  const sessionCounts = availableBundles
    .map((b) => b.session_count)
    .filter((n) => n > 0)
    .sort((a, b) => a - b);

  const formatExpiry = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(d).replace('.', '');
    } catch {
      return iso.slice(0, 10);
    }
  };

  return (
    <div className="booking-step bk-step bk-bundles">
      <div className="bk-step-head">
        <p className="bk-step-eyebrow">{t('booking.bundles_eyebrow')}</p>
        <h2 className="bk-step-title">
          {showCatalog ? t('booking.bundles_headline') : t('booking.bundles_title')}
        </h2>
        <p className="bk-step-context">
          {showCatalog && !hasActive
            ? t('booking.bundles_none_active_hint')
            : t('booking.bundles_have_active')}
        </p>
      </div>

      {hasActive && !showCatalog ? (
        <>
          <div className="bk-bundle-active-list">
            {myBundles.map((b) => {
              const total = Math.max(0, b.sessionsTotal);
              const remaining = Math.max(0, Math.min(total, b.sessionsRemaining));
              const expiry = formatExpiry(b.expiresAt);
              return (
                <div key={b.id} className="bk-bundle-active">
                  <div className="bk-bundle-active-top">
                    <div className="bk-bundle-active-text">
                      <span className="bk-bundle-active-name">
                        {t('booking.bundle_session_count', { count: total })}
                      </span>
                      {expiry ? (
                        <span className="bk-bundle-active-expiry">
                          {t('booking.bundle_expires_on', { date: expiry })}
                        </span>
                      ) : null}
                    </div>
                    <div className="bk-bundle-active-count">
                      <strong>{remaining}</strong>
                      <span>/ {total}</span>
                    </div>
                  </div>
                  {/* Un segmento por sesión: se ve de un vistazo cuántas quedan, no un porcentaje. */}
                  <div
                    className="bk-bundle-meter"
                    role="img"
                    aria-label={t('booking.bundle_consumed_of_total', {
                      consumed: b.sessionsConsumed,
                      total,
                    })}
                  >
                    {Array.from({ length: total }, (_, i) => (
                      <span
                        key={i}
                        className={`bk-bundle-meter-seg${i >= total - remaining ? ' bk-bundle-meter-seg--left' : ''}`}
                      />
                    ))}
                  </div>

                  {(history?.[b.id]?.length ?? 0) > 0 ? (
                    <>
                      <button
                        type="button"
                        className="bk-link bk-link--accent bk-bundle-history-toggle"
                        aria-expanded={openHistory === b.id}
                        onClick={() => setOpenHistory(openHistory === b.id ? null : b.id)}
                      >
                        {openHistory === b.id
                          ? t('booking.bundles_page_hide_history')
                          : t('booking.bundles_page_view_history', { count: history![b.id].length })}
                      </button>
                      {openHistory === b.id ? (
                        <ul className="bk-bundle-history">
                          {history![b.id].map((row) => (
                            <li key={row.id} className="bk-bundle-history-row">
                              <span
                                className={`bk-bundle-history-delta${row.delta < 0 ? ' bk-bundle-history-delta--out' : ''}`}
                              >
                                {row.delta < 0 ? `−${Math.abs(row.delta)}` : `+${row.delta}`}
                              </span>
                              <span className="bk-bundle-history-label">{row.label}</span>
                              <span className="bk-bundle-history-date">{row.date}</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>

          {canBuy ? (
            <button type="button" className="bk-bundle-buy-row" onClick={() => setBuying(true)}>
              <span className="bk-bundle-buy-icon" aria-hidden>
                <Plus size={17} strokeWidth={2.2} />
              </span>
              <span className="bk-bundle-buy-text">
                <span className="bk-bundle-buy-title">{t('booking.bundles_buy_another')}</span>
                {sessionCounts.length > 0 ? (
                  <span className="bk-bundle-buy-hint">
                    {t('booking.bundles_sizes', {
                      sizes: formatList(sessionCounts, t('booking.bundles_or')),
                    })}
                  </span>
                ) : null}
              </span>
              <ChevronRight className="bk-chevron" size={16} strokeWidth={2.2} aria-hidden />
            </button>
          ) : null}
        </>
      ) : null}

      {showCatalog && canBuy ? (
        <>
          <div className="bk-bundle-options" role="radiogroup" aria-label={t('booking.bundles_buy')}>
            {availableBundles.map((b, index) => {
              const perSession = b.session_count > 0 ? b.price / b.session_count : 0;
              const isOn = selectedId === b.id;
              return (
                <button
                  key={b.id}
                  type="button"
                  role="radio"
                  aria-checked={isOn}
                  className={`bk-bundle-option${isOn ? ' bk-bundle-option--on' : ''}`}
                  onClick={() => setSelectedId(b.id)}
                >
                  <span className="bk-radio" aria-hidden />
                  <span className="bk-bundle-option-text">
                    <span className="bk-bundle-option-name">
                      {b.name || t('booking.bundle_session_count', { count: b.session_count })}
                      {index === recommendedIndex && availableBundles.length > 1 ? (
                        <span className="bk-bundle-tag">{t('booking.bundle_most_chosen')}</span>
                      ) : null}
                    </span>
                    <span className="bk-bundle-option-hint">
                      {b.description
                        ? b.description
                        : [
                            perSession > 0
                              ? t('booking.bundle_per_session', { amount: money(perSession, b.currency, locale) })
                              : '',
                            b.validity_days ? t('booking.bundle_validity_days', { days: b.validity_days }) : '',
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                    </span>
                  </span>
                  <span className="bk-bundle-option-price">{money(b.price, b.currency, locale)}</span>
                </button>
              );
            })}
          </div>

          <div className="bk-section-label bk-section-label--spaced">
            <span>{t('booking.bundles_how_title')}</span>
            <i aria-hidden />
          </div>
          <div className="bk-bundle-howto">
            {['booking.bundles_how_1', 'booking.bundles_how_2', 'booking.bundles_how_3'].map((key) => (
              <p key={key} className="bk-bundle-howto-item">
                <Check size={16} strokeWidth={2.4} aria-hidden />
                {t(key)}
              </p>
            ))}
          </div>

          <div className="bk-review-footer">
            <button
              type="button"
              className="btn-primary bk-review-cta"
              disabled={!selected || busy}
              onClick={() => selected && onPurchase(selected)}
            >
              {busy
                ? t('booking.bundles_page_processing')
                : selected
                ? t('booking.bundles_cta', {
                    count: selected.session_count,
                    amount: money(selected.price, selected.currency, locale),
                  })
                : t('booking.bundles_buy')}
            </button>
            <p className="bk-review-footnote">{t('booking.bundles_cta_note')}</p>
          </div>
        </>
      ) : null}

      {showCatalog && !canBuy ? (
        <p className="bk-step-context">{t('booking.bundles_none_available')}</p>
      ) : null}

      <button
        type="button"
        className="booking-back-btn"
        onClick={() => (buying && hasActive ? setBuying(false) : onBack())}
      >
        ← {buying && hasActive ? t('booking.bundles_title') : t('booking.back_to_menu')}
      </button>
    </div>
  );
}
